/**
 * Team Leader Exam Management Workspace — wraps the backend `api/teamleader/exams/*` endpoints.
 *
 * Every endpoint is authorized `SystemAdmin,TeamLeader` and scoped server-side to the caller's
 * managed groups/students. These are new endpoints and the backend DTOs are genuinely camelCase
 * (.NET System.Text.Json default) — no snake_case translation is needed, but we still define
 * explicit TS interfaces for every response here (never let `any` leak into hooks/components),
 * matching the adapter-in-api-layer discipline used across this codebase.
 */
import { API_BASE_URL, getAuthHeaders } from "@/lib/api-client";
import type { PaginatedResponse } from "@/lib/types";

// ── Shared request helper (mirrors teamleader.ts::request) ─────────────────────
// Attaches auth, surfaces the server's error message, and tolerates empty (204) bodies.
async function request<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  if (!API_BASE_URL) throw new Error("API base URL not configured");
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: { ...getAuthHeaders(), ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      message = body?.message || body?.error || message;
    } catch {
      const text = await res.text().catch(() => "");
      if (text) message = text;
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// ── Query params ─────────────────────────────────────────────────────────────

/**
 * Shared filter + paging query params for the dashboard/analytics-summary/detail/export
 * endpoints. Paging fields (`pageNumber`/`pageSize`/`sortBy`/`sortDir`) are ignored by the
 * summary/export endpoints when present — callers simply omit them for those calls.
 */
export interface TlExamListParams {
  search?: string;
  courseId?: string;
  trackId?: string;
  groupId?: string;
  studentId?: string;
  examType?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

// Builds the query string for all TlExamListParams-shaped calls. Mirrors the manual
// URLSearchParams pattern used by fetchExamsPaged in exams.ts (no shared buildQueryString
// util exists yet in this codebase — each api/*.ts file defines its own local builder).
function buildTlExamsQuery(params: TlExamListParams = {}): string {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.courseId) qs.set("courseId", params.courseId);
  if (params.trackId) qs.set("trackId", params.trackId);
  if (params.groupId) qs.set("groupId", params.groupId);
  if (params.studentId) qs.set("studentId", params.studentId);
  if (params.examType) qs.set("examType", params.examType);
  if (params.status) qs.set("status", params.status);
  if (params.dateFrom) qs.set("dateFrom", params.dateFrom);
  if (params.dateTo) qs.set("dateTo", params.dateTo);
  if (params.pageNumber) qs.set("pageNumber", String(params.pageNumber));
  if (params.pageSize) qs.set("pageSize", String(params.pageSize));
  if (params.sortBy) qs.set("sortBy", params.sortBy);
  if (params.sortDir) qs.set("sortDir", params.sortDir);
  const query = qs.toString();
  return query ? `?${query}` : "";
}

// ── Response shapes ──────────────────────────────────────────────────────────

export interface TlExamListItem {
  examId: string;
  title: string;
  courseName: string;
  trackName?: string | null;
  examType: string;
  assignedCount: number;
  completedCount: number;
  passedCount: number;
  failedCount: number;
  pendingGradingCount: number;
  /**
   * Attempts the expiry sweep closed with zero answers recorded. Optional so an older API build that
   * predates the field reads as "unknown" rather than "0 abandoned".
   */
  abandonedCount?: number;
  averageScore: number;
  highestScore: number;
  lowestScore: number;
  averageDurationSeconds: number;
  activeSessionsCount: number;
  lastAttemptAt?: string | null;
  status: string;
}

export interface TlExamAnalyticsSummary {
  totalStudents: number;
  activeAttempts: number;
  completedAttempts: number;
  passed: number;
  failed: number;
  pendingGrading: number;
  averageScore: number;
  highestScore: number;
  lowestScore: number;
  averageDurationSeconds: number;
  violationRate: number;
  essayPending: number;
  retakeCount: number;
}

export interface TlExamDetailStudentRow {
  sessionId?: string | null;
  studentId: string;
  avatarUrl?: string | null;
  studentName: string;
  email: string;
  examId?: string;
  examTitle?: string;
  groupName?: string | null;
  trackName?: string | null;
  courseName: string;
  attemptNumber: number;
  currentScore?: number | null;
  passed?: boolean | null;
  attemptStatus: string;
  startedAt?: string | null;
  submittedAt?: string | null;
  durationSeconds?: number | null;
  violationCount: number;
  manualGrade?: number | null;
  automaticGrade?: number | null;
  progressPercent: number;
  /**
   * Server-authoritative: may the CALLER grade this attempt? Decided by
   * `IAccessScopeService.GetGradeAuthorizationAsync` — the same authority the grading endpoints
   * enforce.
   *
   * Never re-derive this in the client. Grading scope is NARROWER than the scope that produced the
   * row: appearing in this roster only means the caller may view and moderate the attempt, whereas
   * grading additionally needs a CourseInstructor assignment or one group that the caller leads,
   * that holds this student, AND that delivers this exam's course. The browser cannot see
   * group→course delivery links, so any client-side guess would be wrong.
   *
   * Optional so an older API build (which omits it) degrades to "unknown" rather than "forbidden" —
   * see `canGradeRow` in the exam detail route for how that is resolved.
   */
  canGrade?: boolean;
  /** Caller-facing reason `canGrade` is false. Null/absent when it is true. */
  gradeBlockedReason?: string | null;
  /**
   * Whether this exam is anchored to a progression checkpoint (whole-module or in-video), and so
   * whether the two content-resetting actions apply to it — both define "the content after this
   * exam" from the gate, which a course or track final does not have.
   *
   * Server-sent, not derived from `examType`: anchoring is structural and the type enum has no
   * in-video member, so the browser cannot tell the cases apart. Optional so an older API build
   * degrades to "offer it and let the server refuse".
   */
  isCheckpointAnchored?: boolean;
  /**
   * What kind of final this exam is: "None" | "CourseFinal" | "TrackFinal" | "WeekFinal".
   * The enum NAME, so a category added server-side cannot be misread as an existing one.
   */
  examFinalCategory?: string;
  /**
   * Whether the server will accept the rewatch reattempt for this row. False for every kind of
   * final — the hard business rule — and false for an exam that gates nothing.
   *
   * Server-computed from the same predicates the write path enforces, so the menu cannot offer an
   * operation the endpoint refuses. Hiding the item is an affordance, never the enforcement.
   */
  canReattemptWithRewatch?: boolean;
  /** Whether the progress-preserving reattempt may be offered. True for every exam, finals included. */
  canReattemptPreservingProgress?: boolean;
  /**
   * Whether the server will accept the standalone "reset the content after this exam" operation —
   * the downstream reset with no new attempt and no change to the recorded result. False under the
   * same structural conditions as `canReattemptWithRewatch`, and carried separately because the two
   * are separately authorized endpoints.
   */
  canResetContentAfterExam?: boolean;
  /** Why `canReattemptWithRewatch` is false, in operator-facing wording. */
  rewatchBlockedReason?: string | null;
  /**
   * When this sitting's pass stopped counting towards progression, because a reattempt was
   * authorized over it. Absent/null on every ordinary attempt.
   *
   * Read it ALONGSIDE `passed`, never instead of it: both are true at once — the sitting WAS a pass,
   * and it no longer opens the checkpoint. A row showing a green "Passed" with this set belongs to a
   * student who is (correctly) still locked out of the content behind that exam, and before this
   * field existed there was no way to tell that from any staff screen.
   */
  progressionSupersededAt?: string | null;
  /** Reason the operator gave when superseding the pass. Null when none was given. */
  progressionSupersededReason?: string | null;
}

/**
 * `StudentExamSessionDto` (LMSAPI.BLL.Dtos.Exam.ExamAnalyticsDtos) — one attempt in a
 * student's exam history, as nested under `TlStudentExamProfileDto.ExamHistory`.
 */
export interface TlExamHistoryEntry {
  sessionId: string;
  examId: string;
  examTitle: string;
  status: string;
  score?: number | null;
  passed?: boolean | null;
  violationCount: number;
  startedAt: string;
  completedAt?: string | null;
}

/** `CertificateRefDto` — course/track back-reference on a certificate. */
export interface TlCertificateRef {
  id: string;
  title?: string | null;
  name?: string | null;
}

/**
 * `CertificateUserDto` — note `full_name` (not `fullName`): `CertificateDto` and its nested
 * DTOs use explicit `[JsonPropertyName]` snake_case overrides (this DTO is shared with the
 * public /verify/:certCode contract), unlike the rest of this file's plain-camelCase payloads.
 */
export interface TlCertificateUser {
  id: string;
  email?: string | null;
  full_name?: string | null;
}

/** `CertificateDto` — snake_case field names per its `[JsonPropertyName]` overrides. */
export interface TlCertificate {
  id: string;
  cert_code: string;
  cert_type: string;
  status: string;
  title: string;
  custom_title?: string | null;
  issued_at?: string | null;
  created_at: string;
  custom_hours?: number | null;
  score?: number | null;
  pdf_url?: string | null;
  verification_url?: string | null;
  batch_id?: string | null;
  course?: TlCertificateRef | null;
  track?: TlCertificateRef | null;
  user?: TlCertificateUser | null;
}

/** `TlCheckpointProgressDto` — a student's standing on one course's checkpoints. */
export interface TlCheckpointProgress {
  courseId: string;
  courseName: string;
  currentCheckpointName?: string | null;
  checkpointStatus: string;
}

/** `TlStudentModuleRefDto` — one module of a course plus this student's standing on it. */
export interface TlStudentModuleRef {
  moduleId: string;
  title: string;
  orderIndex: number;
  /** "NotStarted" | "InProgress" | "Completed" */
  status: string;
  progressPercentage: number;
  /** Credited time-on-content, never wall-clock. */
  activeWatchSeconds: number;
  completedAt?: string | null;
  lastAccessedAt?: string | null;
}

/**
 * `TlStudentCourseProgressDto` — per-course completion detail for one student.
 *
 * Covers every course the student can actually reach, including group- and track-derived access
 * that creates no Enrollment row. `completionPercent` is the module-based progress bar;
 * `isCompleted` additionally requires every exam gate to be passed, so a course can sit at 100 %
 * and still not be complete.
 */
export interface TlStudentCourseProgress {
  courseId: string;
  courseTitle: string;
  thumbnailUrl?: string | null;
  /** "track" | "standalone" */
  source: string;
  trackId?: string | null;
  trackName?: string | null;
  trackSequence?: number | null;

  completionPercent: number;
  isCompleted: boolean;
  /** "not_started" | "in_progress" | "completed" */
  enrollmentStatus: string;

  totalModules: number;
  completedModules: number;
  inProgressModules: number;

  watchedHours: number;
  totalDurationSeconds: number;

  /** Most recently opened module — "where are they now". */
  lastAccessedModule?: TlStudentModuleRef | null;
  /** Most recently finished module. */
  lastCompletedModule?: TlStudentModuleRef | null;
  /** Earliest unfinished module in course order — what they should do next. */
  nextModule?: TlStudentModuleRef | null;

  lastActivityAt?: string | null;

  currentCheckpointName?: string | null;
  checkpointStatus?: string | null;

  isLocked: boolean;
  lockReason?: string | null;
  blockingCourseName?: string | null;
}

/** `ActivityLogListItemDto` — one row of `TlStudentExamProfileDto.RecentActivity`. */
export interface TlActivityLogItem {
  id: string;
  timestamp: string;
  userId?: string | null;
  userName?: string | null;
  email?: string | null;
  role?: string | null;
  actionType: string;
  entityType?: string | null;
  entityId?: string | null;
  entityName?: string | null;
  isSuccess: boolean;
  ipAddress?: string | null;
  browser?: string | null;
  operatingSystem?: string | null;
  durationMs?: number | null;
}

/** `TlStudentExamProfileDto` — the single-student drill-down opened from an exam roster row. */
export interface TlStudentExamProfile {
  studentId: string;
  studentName: string;
  email: string;
  examHistory: TlExamHistoryEntry[];
  totalAttempts: number;
  successRatePercent: number;
  failedExamCount: number;
  averageScore: number;
  learningHours: number;
  certificates: TlCertificate[];
  checkpointProgress: TlCheckpointProgress[];
  currentCourses: string[];
  currentTracks: string[];
  /**
   * The same reachable courses as `currentCourses`, with full completion detail instead of just a
   * title. Prefer this over `currentCourses` for anything richer than a name.
   */
  courseProgress?: TlStudentCourseProgress[];

  // Rollups over DISTINCT courses (a course in two tracks is counted once).
  coursesAssigned?: number;
  coursesCompleted?: number;
  coursesInProgress?: number;
  coursesNotStarted?: number;
  totalModulesCompleted?: number;
  totalModulesAssigned?: number;
  /** Module-weighted, NOT the average of the per-course bars. */
  overallCompletionPercent?: number;
  /** Share of courses whose exam gates are all met. */
  courseCompletionRatePercent?: number;
  /** Most recent module activity across every course. */
  lastActivityAt?: string | null;

  weakCategories: string[];
  strongCategories: string[];
  recentActivity: TlActivityLogItem[];
  /**
   * Exams assigned/available to the student right now, including ones never attempted.
   * `examHistory` above only contains attempts.
   */
  assignedExams?: TlAssignedExam[];
}

/** One row of `TlStudentExamProfileDto.AssignedExams` — mirrors `AvailableExamDto`. */
export interface TlAssignedExam {
  id: string;
  title: string;
  examType: string;
  passingScore: number;
  timeLimitMinutes: number;
  maxAttempts: number;
  attemptsUsed: number;
  attemptsRemaining: number;
  isLocked: boolean;
  lockReason?: string | null;
  withinWindow: boolean;
  startDate?: string | null;
  endDate?: string | null;
  lastScore?: number | null;
  lastPassed?: boolean | null;
  lastStatus?: string | null;
  courseId?: string | null;
  courseTitle?: string | null;
}

export interface BulkActionFailure {
  id: string;
  reason: string;
}

export interface BulkActionResult {
  succeededCount: number;
  failedCount: number;
  failures: BulkActionFailure[];
}

// ── Mutation request bodies ──────────────────────────────────────────────────

export interface TlAllowRetakeRequest {
  examId: string;
  studentId: string;
  reason?: string;
}

export interface TlBulkRetakeTarget {
  examId: string;
  studentId: string;
}

export interface TlBulkResetRequest {
  sessionIds: string[];
  reason?: string;
}

export interface TlBulkRetakeRequest {
  targets: TlBulkRetakeTarget[];
  reason?: string;
}

export interface RetakeGrant {
  id: string;
}

// ── Bulk message ─────────────────────────────────────────────────────────────

/**
 * `BulkMessageRequestDto` — `targetType` selects which of `studentIds`/`groupId`/`courseId`/
 * `trackId` is used to resolve recipients server-side; only the field matching `targetType` is
 * read by the backend, but all are optional here so callers can build one object regardless of
 * which target mode is active.
 */
export interface TlBulkMessageRequest {
  targetType: "students" | "group" | "course" | "track";
  studentIds?: string[];
  groupId?: string;
  courseId?: string;
  trackId?: string;
  content: string;
  requiresAcknowledgment: boolean;
}

// ── Audit history ─────────────────────────────────────────────────────────────

/**
 * Query params for `GET /teamleader/exams/audit-history`, mirroring `ActivityLogQueryDto`
 * (LMSAPI.BLL.Dtos.ActivityLog.ActivityLogDtos). Note this DTO's paging/sort fields diverge from
 * `TlExamListParams`: `page` (not `pageNumber`) and `sortDesc: boolean` (not `sortDir`
 * "asc"|"desc"). `userId`/`userScope` are deliberately omitted — the server always forces
 * "caller's own actions only" for this route and does not read a client-supplied user filter.
 */
export interface TlAuditHistoryParams {
  role?: string;
  actionType?: string;
  entityType?: string;
  entityId?: string;
  dateFrom?: string;
  dateTo?: string;
  isSuccess?: boolean;
  ip?: string;
  browser?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDesc?: boolean;
}

function buildTlAuditHistoryQuery(params: TlAuditHistoryParams = {}): string {
  const qs = new URLSearchParams();
  if (params.role) qs.set("role", params.role);
  if (params.actionType) qs.set("actionType", params.actionType);
  if (params.entityType) qs.set("entityType", params.entityType);
  if (params.entityId) qs.set("entityId", params.entityId);
  if (params.dateFrom) qs.set("dateFrom", params.dateFrom);
  if (params.dateTo) qs.set("dateTo", params.dateTo);
  if (params.isSuccess !== undefined) qs.set("isSuccess", String(params.isSuccess));
  if (params.ip) qs.set("ip", params.ip);
  if (params.browser) qs.set("browser", params.browser);
  if (params.search) qs.set("search", params.search);
  if (params.page) qs.set("page", String(params.page));
  if (params.pageSize) qs.set("pageSize", String(params.pageSize));
  if (params.sortBy) qs.set("sortBy", params.sortBy);
  if (params.sortDesc !== undefined) qs.set("sortDesc", String(params.sortDesc));
  const query = qs.toString();
  return query ? `?${query}` : "";
}

// ── Student attempt timeline ────────────────────────────────────────────────

/** Query params for `GET /teamleader/exams/students/{studentId}/timeline` (`TlTimelineFilterDto`). */
export interface TlStudentTimelineParams {
  examId?: string;
  eventType?: string;
  dateFrom?: string;
  dateTo?: string;
  pageNumber?: number;
  pageSize?: number;
}

function buildTlStudentTimelineQuery(params: TlStudentTimelineParams = {}): string {
  const qs = new URLSearchParams();
  if (params.examId) qs.set("examId", params.examId);
  if (params.eventType) qs.set("eventType", params.eventType);
  if (params.dateFrom) qs.set("dateFrom", params.dateFrom);
  if (params.dateTo) qs.set("dateTo", params.dateTo);
  if (params.pageNumber) qs.set("pageNumber", String(params.pageNumber));
  if (params.pageSize) qs.set("pageSize", String(params.pageSize));
  const query = qs.toString();
  return query ? `?${query}` : "";
}

/**
 * `TlStudentTimelineEventDto` — one row of a student's cross-source attempt timeline (merges
 * `ExamAuditLogEntry` session-lifecycle rows with synthesized retake-grant rows).
 */
export interface TlStudentTimelineEvent {
  sessionId?: string | null;
  examId?: string | null;
  examTitle?: string | null;
  eventType: string;
  detail?: string | null;
  occurredAt: string;
  actorUserId?: string | null;
  actorName?: string | null;
  /** See `TlExamDetailStudentRow.isCheckpointAnchored`. */
  isCheckpointAnchored?: boolean;
  /**
   * What kind of final this exam is: "None" | "CourseFinal" | "TrackFinal" | "WeekFinal".
   * The enum NAME, so a category added server-side cannot be misread as an existing one.
   */
  examFinalCategory?: string;
  /**
   * Whether the server will accept the rewatch reattempt for this row. False for every kind of
   * final — the hard business rule — and false for an exam that gates nothing.
   *
   * Server-computed from the same predicates the write path enforces, so the menu cannot offer an
   * operation the endpoint refuses. Hiding the item is an affordance, never the enforcement.
   */
  canReattemptWithRewatch?: boolean;
  /** Whether the progress-preserving reattempt may be offered. True for every exam, finals included. */
  canReattemptPreservingProgress?: boolean;
  /**
   * Whether the server will accept the standalone "reset the content after this exam" operation —
   * the downstream reset with no new attempt and no change to the recorded result. False under the
   * same structural conditions as `canReattemptWithRewatch`, and carried separately because the two
   * are separately authorized endpoints.
   */
  canResetContentAfterExam?: boolean;
  /** Why `canReattemptWithRewatch` is false, in operator-facing wording. */
  rewatchBlockedReason?: string | null;
}

// ── Students at risk ─────────────────────────────────────────────────────────

/** Query params for `GET /teamleader/exams/at-risk` (`TlAtRiskFilterDto`). */
export interface TlAtRiskParams {
  courseId?: string;
  groupId?: string;
  minRiskFlags?: number;
  pageNumber?: number;
  pageSize?: number;
}

function buildTlAtRiskQuery(params: TlAtRiskParams = {}): string {
  const qs = new URLSearchParams();
  if (params.courseId) qs.set("courseId", params.courseId);
  if (params.groupId) qs.set("groupId", params.groupId);
  if (params.minRiskFlags !== undefined) qs.set("minRiskFlags", String(params.minRiskFlags));
  if (params.pageNumber) qs.set("pageNumber", String(params.pageNumber));
  if (params.pageSize) qs.set("pageSize", String(params.pageSize));
  const query = qs.toString();
  return query ? `?${query}` : "";
}

/** `TlAtRiskStudentDto` — one row of the "Students At Risk" widget. */
export interface TlAtRiskStudent {
  studentId: string;
  studentName: string;
  email: string;
  groupName?: string | null;
  failingGradesFlag: boolean;
  recentFailCount: number;
  recentPassRate?: number | null;
  lowActivityFlag: boolean;
  lastActiveAt?: string | null;
  checkpointRiskFlag: boolean;
  failedCheckpointCount: number;
  violationRiskFlag: boolean;
  totalViolations: number;
  riskScore: number;
}

// ── Per-exam insights ────────────────────────────────────────────────────────

/**
 * `QuestionInsightDto` (LMSAPI.BLL.Dtos.Exam.ExamAnalyticsDtos) — full per-question analytics,
 * reused as-is (not duplicated) by the TeamLeader exam-insights and per-exam-questions endpoints.
 * There is no `correctCount` field on the backend DTO — only `percentCorrect`/`percentWrong`.
 */
export interface TlQuestionInsight {
  questionId: string;
  questionText: string;
  category?: string | null;
  difficulty?: string | null;
  timesAsked: number;
  timesAnswered: number;
  timesSkipped: number;
  percentCorrect: number;
  percentWrong: number;
  avgResponseSeconds: number;
  mostSelectedWrongAnswer?: string | null;
}

/** `TlStudentInsightDto` — generic (student, value) shape reused for fastest/slowest/most-violations. */
export interface TlStudentInsight {
  studentId: string;
  studentName: string;
  value: number;
}

/** `TlGroupInsightDto` — a group's standing on one exam. */
export interface TlGroupInsight {
  groupId: string;
  groupName: string;
  averageScore: number;
  studentCount: number;
}

/** `TlExamInsightsDto` — per-exam supervision insights. */
export interface TlExamInsights {
  examId: string;
  averageDurationSeconds: number;
  mostFailedQuestions: TlQuestionInsight[];
  mostSkippedQuestions: TlQuestionInsight[];
  fastestStudent?: TlStudentInsight | null;
  slowestStudent?: TlStudentInsight | null;
  highestGroup?: TlGroupInsight | null;
  lowestGroup?: TlGroupInsight | null;
  mostViolationsStudent?: TlStudentInsight | null;
}

/**
 * `TlExamSummaryDto` — the single-exam header/Overview payload
 * (`GET /teamleader/exams/{examId}/summary`, added in Phase 1.5-A).
 *
 * Why this exists rather than `GET /exams/{examId}`: that endpoint's *role* gate admits TeamLeader
 * but its *scope* gate (`CanViewExamAsync`) reads authorship ∪ CourseInstructor only, so it 403s a
 * leader whose reach comes from a group — exactly the caller this workspace serves. Its
 * authorization reuses the roster's own `GetViewableExamAsync` gate, so "can open the roster" and
 * "can read this header" are the same right by construction.
 *
 * `courseId`/`trackId` are the fields that retire the old name-matching id lookup: the header's
 * "Message by Course/Track" action used to recover ids by matching the course NAME against the
 * caller's assignable lists, and silently disabled itself whenever no name matched.
 */
export interface TlExamSummary {
  examId: string;
  title: string;
  examType: string;
  /** Exam-level facet derived from IsActive/StartDate/EndDate: "Active" | "Closed" | "Upcoming". */
  status: string;
  /** Scheduling window — unobtainable from any other endpoint available to this caller. */
  startDate?: string | null;
  endDate?: string | null;
  courseId?: string | null;
  courseName?: string | null;
  trackId?: string | null;
  trackName?: string | null;
  assignedCount: number;
  completedCount: number;
  passedCount: number;
  failedCount: number;
  pendingGradingCount: number;
  /**
   * Attempts the expiry sweep closed with **zero answers recorded** — the only way a session becomes
   * `Abandoned` (`ExamService.FinalizeExpiredSessionsAsync`). An expired sitting that had answers is
   * auto-submitted instead, so this never overlaps `completedCount`.
   *
   * Exam-wide and unfiltered, computed from the same session list every other metric here comes
   * from — so it is safe to show as a KPI, unlike a count derived from the paginated roster.
   *
   * Optional so an older API build that predates it degrades to "no KPI" rather than "0 abandoned".
   */
  abandonedCount?: number;
  averageScore: number;
  highestScore?: number | null;
  lowestScore?: number | null;
  averageDurationSeconds: number;
  activeSessionsCount: number;
  lastAttemptAt?: string | null;
}

// ── Reads ─────────────────────────────────────────────────────────────────────

export const fetchTlExamSummary = (examId: string) =>
  request<TlExamSummary>(`/teamleader/exams/${examId}/summary`);

export const fetchTlExamsDashboard = (params: TlExamListParams = {}) =>
  request<PaginatedResponse<TlExamListItem>>(`/teamleader/exams${buildTlExamsQuery(params)}`);

export const fetchTlAnalyticsSummary = (params: TlExamListParams = {}) =>
  request<TlExamAnalyticsSummary>(
    `/teamleader/exams/analytics-summary${buildTlExamsQuery(params)}`,
  );

export const fetchTlExamDetail = (examId: string, params: TlExamListParams = {}) =>
  request<PaginatedResponse<TlExamDetailStudentRow>>(
    `/teamleader/exams/${examId}${buildTlExamsQuery(params)}`,
  );

export const fetchTlAllAttempts = (params: TlExamListParams = {}) =>
  request<PaginatedResponse<TlExamDetailStudentRow>>(
    `/teamleader/exams/attempts${buildTlExamsQuery(params)}`,
  );

export const fetchTlStudentExamProfile = (studentId: string) =>
  request<TlStudentExamProfile>(`/teamleader/exams/students/${studentId}/profile`);

export const fetchTlAuditHistory = (params: TlAuditHistoryParams = {}) =>
  request<PaginatedResponse<TlActivityLogItem>>(
    `/teamleader/exams/audit-history${buildTlAuditHistoryQuery(params)}`,
  );

export const fetchTlStudentTimeline = (studentId: string, params: TlStudentTimelineParams = {}) =>
  request<PaginatedResponse<TlStudentTimelineEvent>>(
    `/teamleader/exams/students/${studentId}/timeline${buildTlStudentTimelineQuery(params)}`,
  );

export const fetchTlAtRiskStudents = (params: TlAtRiskParams = {}) =>
  request<PaginatedResponse<TlAtRiskStudent>>(
    `/teamleader/exams/at-risk${buildTlAtRiskQuery(params)}`,
  );

export const fetchTlExamInsights = (examId: string) =>
  request<TlExamInsights>(`/teamleader/exams/${examId}/insights`);

export const fetchTlExamQuestionInsights = (examId: string) =>
  request<TlQuestionInsight[]>(`/teamleader/exams/${examId}/questions`);

// Blob-download trigger — mirrors question-import.ts::downloadImportTemplate exactly
// (create an <a>, click it, revoke the object URL).
export async function downloadTlExamExportCsv(
  examId: string,
  params: TlExamListParams = {},
): Promise<void> {
  if (!API_BASE_URL) throw new Error("API base URL not configured");
  const headers = getAuthHeaders();
  delete headers["Content-Type"];

  const res = await fetch(
    `${API_BASE_URL}/teamleader/exams/${examId}/export.csv${buildTlExamsQuery(params)}`,
    { headers },
  );

  if (!res.ok) {
    const errBody = await res.text();
    throw new Error(errBody || "Failed to download export");
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `exam-${examId}-export.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export const resetTlAttempt = (sessionId: string, reason?: string) =>
  request<void>(`/teamleader/exams/sessions/${sessionId}/reset`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });

export const allowTlRetake = (examId: string, studentId: string, reason?: string) =>
  request<RetakeGrant>(`/teamleader/exams/retake`, {
    method: "POST",
    body: JSON.stringify({ examId, studentId, reason }),
  });

/**
 * Operation 1 — authorize a new attempt AND reset the student's progress through the content after
 * the exam, so they work through it again once they re-pass.
 *
 * Refused server-side for every kind of final exam; the client guard is an affordance only.
 */
export const reattemptWithRewatch = (sessionId: string, reason?: string) =>
  request<void>(`/teamleader/exams/sessions/${sessionId}/reattempt-with-rewatch`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });

/**
 * Operation 2 — authorize a new attempt and re-lock the content after the exam, leaving every bit of
 * downstream progress exactly as it stands. Available for every exam type, finals included.
 */
export const reattemptKeepProgress = (sessionId: string, reason?: string) =>
  request<void>(`/teamleader/exams/sessions/${sessionId}/reattempt-keep-progress`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });

/**
 * Reset the student's progress through the content after the exam, and nothing else. No new attempt,
 * no change to the recorded result, and the gate stays open — the content is simply back to unwatched.
 *
 * Refused server-side for every kind of final exam and for an exam that gates nothing, by the same
 * rule that governs `reattemptWithRewatch`; the client guard is an affordance only.
 */
export const resetContentAfterExam = (sessionId: string, reason?: string) =>
  request<void>(`/teamleader/exams/sessions/${sessionId}/reset-content-after`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });

export const forceSubmitTlExam = (sessionId: string) =>
  request<void>(`/teamleader/exams/sessions/${sessionId}/force-submit`, {
    method: "POST",
    body: JSON.stringify({}),
  });

export const bulkResetTlAttempts = (sessionIds: string[], reason?: string) =>
  request<BulkActionResult>(`/teamleader/exams/bulk/reset`, {
    method: "POST",
    body: JSON.stringify({ sessionIds, reason }),
  });

export const bulkAllowTlRetake = (targets: TlBulkRetakeTarget[], reason?: string) =>
  request<BulkActionResult>(`/teamleader/exams/bulk/retake`, {
    method: "POST",
    body: JSON.stringify({ targets, reason }),
  });

export const sendTlBulkMessage = (payload: TlBulkMessageRequest) =>
  request<BulkActionResult>(`/teamleader/exams/bulk/message`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
